/**
 * Engine Types
 *
 * Core type definitions for the database engine system.
 * This file is process-agnostic (no React/Electron imports) so both
 * renderer and main process can safely import these types.
 */

/**
 * Supported database engine identifiers.
 * This is the single source of truth for all engine IDs in the app.
 */
export type DatabaseEngineId = 'mssql' | 'pgsql' | 'sqlite' | 'mysql' | 'oracle' | 'kusto' | 'azurestorage' | 'mock';

/**
 * User-facing engine IDs (excludes 'mock' which is for testing only).
 * Use this type for UI dropdowns and connection forms.
 */
export type UserFacingEngineId = Exclude<DatabaseEngineId, 'mock'>;

/**
 * All user-facing engine IDs as a const array.
 */
export const USER_FACING_ENGINE_IDS: readonly UserFacingEngineId[] = ['mssql', 'pgsql', 'sqlite', 'mysql', 'oracle', 'kusto', 'azurestorage'] as const;

/**
 * All supported engine IDs as a const array for iteration.
 */
export const DATABASE_ENGINE_IDS: readonly DatabaseEngineId[] = [
  'mssql',
  'pgsql',
  'sqlite',
  'mysql',
  'oracle',
  'kusto',
  'azurestorage',
  'mock',
] as const;

/**
 * MySQL/MariaDB variant type.
 * Detected post-connection via `SELECT VERSION()`.
 * Stored on connection state and used for variant-specific branching.
 */
export type MysqlVariant = 'mysql' | 'mariadb';

/**
 * Known parity gap features - use string literals to prevent "same gap, different spelling".
 * When documenting a parity gap, use one of these feature names.
 */
export type ParityGapFeature =
  | 'server-side-cancellation'
  | 'batched-result-fetching'
  | 'multiple-result-sets'
  | 'windows-auth'
  | 'go-batch-separator'
  | 'streaming-results'
  | 'prepared-statements'
  | 'diagnostics'
  | 'actual-execution-plan';

/**
 * Documents a known gap vs other engines.
 * Uses ParityGapFeature string literal to prevent inconsistent naming.
 */
export type ParityGap = {
  readonly feature: ParityGapFeature;
  readonly description: string;
  readonly severity: 'critical' | 'important' | 'minor';
  readonly workaround?: string;
  readonly issueLink?: string;
};

/**
 * Query execution capabilities.
 * NOTE: Capabilities should reflect CURRENT implementation, not theoretical support.
 */
export type QueryExecutionCapabilities = {
  readonly supportsMultipleResultSets: boolean;
  readonly supportsServerSideCancellation: boolean;
  readonly supportsBatchedResultFetching: boolean;
  readonly supportsStreamingResults: boolean;
  readonly supportsCursors: boolean;
  readonly supportsBatchSeparatorGo: boolean;
  readonly supportsTransactions: boolean;
  readonly supportsPreparedStatements: boolean;
};

/**
 * Language service capabilities (IntelliSense).
 */
export type LanguageServiceCapabilities = {
  readonly hasLanguageServer: boolean;
  readonly languageServerType: 'sql-tools-service' | 'postgres-lsp' | 'monaco-kusto' | 'mock' | 'none';
  readonly supportsCompletions: boolean;
  readonly supportsHover: boolean;
  readonly supportsSignatureHelp: boolean;
  readonly supportsDiagnostics: boolean;
  readonly supportsFormatting: boolean;
};

/**
 * Scripting capabilities (DDL generation).
 */
export type ScriptingCapabilities = {
  readonly supportsCreateTable: boolean;
  readonly supportsAlterTable: boolean;
  readonly supportsCreateIndex: boolean;
  readonly supportsCreateView: boolean;
  readonly supportsCreateProcedure: boolean;
  readonly supportsCreateFunction: boolean;
};

/**
 * Connection capabilities.
 */
export type ConnectionCapabilities = {
  readonly supportsWindowsAuth: boolean;
  readonly supportsSSL: boolean;
  readonly supportsConnectionPooling: boolean;
  readonly supportsDatabaseSelection: boolean;
  readonly supportsSchemas: boolean;
};

/**
 * Table Explorer capabilities.
 * These flags drive UI/UX gating for Table Explorer features.
 */
export type TableExplorerCapabilities = {
  /**
   * Whether the data source supports generating a textual SQL preview (SELECT + DML).
   * SQL engines: true. Non-SQL sources (future): false.
   */
  readonly supportsQueryPreview: boolean;
  /**
   * Whether the engine supports inbound FK exploration ("Referenced by") in Table Explorer.
   * This requires engine-specific catalog queries to discover inbound foreign keys.
   */
  readonly supportsInboundForeignKeyExplorer: boolean;
  /** False for append-only engines (kusto): hides all Table Explorer edit affordances and disables change generation. */
  readonly supportsEditing: boolean;
};

/**
 * Security management capabilities.
 * These flags drive UI/UX gating for Security Manager features.
 */
export type SecurityCapabilities = {
  /**
   * Master flag - whether the engine supports security management at all.
   * If false, no security folders or forms will be shown.
   */
  readonly supportsSecurityManagement: boolean;
  /**
   * Whether the engine has server-level principals (logins, server roles).
   * MSSQL: true (logins, server roles). PostgreSQL: true (roles). SQLite: false.
   */
  readonly supportsServerLevelPrincipals: boolean;
  /**
   * Whether the engine has database-level users separate from logins.
   * MSSQL: true (users mapped to logins). PostgreSQL: false (roles are server-level).
   */
  readonly supportsDatabaseUsers: boolean;
  /**
   * Whether the engine supports DENY permissions.
   * MSSQL: true. PostgreSQL: false (only GRANT/REVOKE).
   */
  readonly supportsDeny: boolean;
};

/**
 * SQL IntelliSense capabilities.
 * Drives orchestrator dispatch — each per-engine slice flips these as its
 * resolver lands. Booleans default to `false` until the engine's resolver
 * implements the corresponding behavior.
 */
export type SqlIntellisenseEngine = {
  /** How the engine builds its CST/AST for context derivation. */
  readonly structuralParser: 'tree-sitter' | 'antlr' | 'none';
  /** How the engine produces semantic candidates from that parse tree. */
  readonly semanticParser: 'antlr-c3' | 'tree-sitter-query' | 'none';
  /** FK-driven JOIN snippet generation is wired up. */
  readonly supportsFkDrivenJoin: boolean;
  /** Alias-dot resolution (anti-SOUNDEX: alias-dot returns ONLY that relation's columns). */
  readonly supportsAliasResolution: boolean;
  /** CTE name + body scope is tracked. */
  readonly supportsCteScope: boolean;
  /** Package-qualified procedure references (Oracle PL/SQL). */
  readonly supportsPackageProc: boolean;
  /** Keyword casing (UPPER/lower/Title) is user-configurable for this engine. */
  readonly keywordCasingConfigurable: boolean;
};

/**
 * Cross-cutting workspace-surface capabilities.
 * These flags gate whole workspace tabs/features that aren't specific to
 * query execution, table explorer, or security (e.g. Notebooks, Schema Overview).
 */
export type WorkspaceFeatureCapabilities = {
  /**
   * Whether the engine supports notebook session execution (multi-cell SQL notebooks).
   * All engines, incl. Kusto (stateless per-cell sessions, notebookKustoSession.ts).
   */
  readonly supportsNotebooks: boolean;
  /**
   * Whether the engine supports the Schema Overview (ERD) workspace tab.
   * Kusto: false (electron schemaOverviewService has no kusto catalog queries yet).
   */
  readonly supportsSchemaOverview: boolean;
  /**
   * Whether the engine has a Query Editor tab at all (a query language the
   * app can execute). Every SQL engine + kusto + mock: true. Azure Storage: false
   * (no query language; the storage browser + storage_* tools are its surface).
   */
  readonly supportsQueryEditor: boolean;
  /**
   * Whether the engine opens the Table Explorer TAB for its objects. Every SQL
   * engine + kusto + mock: true. Azure Storage: false (entities render inside
   * the storage-explorer tab through the TableExplorerGrid host seam).
   */
  readonly supportsTableExplorer: boolean;
};

/**
 * Spatial / geometry capabilities.
 * Gate spatial column detection, grid summaries + map surfaces, and spatial
 * filters. `supportsSpatialTypes` covers viewing (all SQL engines can hold and
 * present geometry); `supportsSpatialFilters` covers emitting spatial WHERE
 * predicates (SQLite starts false and a per-connection SpatiaLite probe upgrades
 * it at runtime — T15).
 */
export type SpatialCapabilities = {
  readonly supportsSpatialTypes: boolean;
  readonly supportsSpatialFilters: boolean;
};

/**
 * All capability categories and their types.
 */
export type CapabilityCategories = {
  queryExecution: QueryExecutionCapabilities;
  languageServices: LanguageServiceCapabilities;
  scripting: ScriptingCapabilities;
  connection: ConnectionCapabilities;
  tableExplorer: TableExplorerCapabilities;
  security: SecurityCapabilities;
  intellisense: SqlIntellisenseEngine;
  workspaceFeatures: WorkspaceFeatureCapabilities;
  spatial: SpatialCapabilities;
};

/**
 * Stable reason classification for a dangerous-query verdict (WS-E / CK-E).
 * Locked by the dangerous-gate equivalence fixtures — treat renames as policy
 * changes, not refactors.
 *
 * - `parse-ambiguous`: the buffer could not be scanned confidently.
 * - `destructive`: destroys data or restructures the schema in place.
 * - `routine-drop`: drops a code/routine object (no rows lost).
 * - `unbounded-dml`: DELETE/UPDATE (or tautology-filtered DML) hitting all rows.
 * - `system-command`: server-level command affecting stability/config.
 * - `unsupported-management`: management command the adapter refuses (kusto).
 */
export type RiskReasonClass =
  | 'parse-ambiguous'
  | 'destructive'
  | 'routine-drop'
  | 'unbounded-dml'
  | 'system-command'
  | 'unsupported-management';

/**
 * A dangerous-query pattern tagged with a stable id, its severity tier, and
 * its reason class.
 *
 * - `high`: destroys data or restructures the schema in place
 *   (DROP TABLE, TRUNCATE, ALTER TABLE … DROP, DELETE/UPDATE without
 *   WHERE, engine-specific destructive admin commands).
 * - `low`:  drops a code/routine object (DROP PROCEDURE, DROP FUNCTION,
 *   DROP TRIGGER, DROP VIEW, DROP PACKAGE/TYPE on Oracle). Real but
 *   doesn't lose rows — the dialog shows a milder warning.
 */
export type DangerousPattern = {
  /** Stable rule id (`mssql.dbcc-shrink`) — internal, but referenced by tests. */
  readonly id: string;
  readonly pattern: RegExp;
  readonly severity: 'high' | 'low';
  readonly reasonClass: RiskReasonClass;
};

/** A DML pattern that is dangerous only without an effective row filter
 *  (always high severity, always `unbounded-dml`). */
export type UnboundedDmlPattern = {
  readonly id: string;
  readonly pattern: RegExp;
};

/** An engine-specific always-dangerous pattern (always high severity). */
export type EngineSpecificPattern = {
  readonly id: string;
  readonly pattern: RegExp;
  readonly reasonClass: RiskReasonClass;
};

/**
 * Dangerous query patterns that require confirmation.
 * Engine-specific patterns for safety checks. Evaluated by the risk gate
 * (`src/features/sql-safety/riskGate.ts`) per grammar statement slice over a
 * code-only projection — never against a raw multi-statement buffer.
 */
export type DangerousQueryPatterns = {
  /** Patterns that always require confirmation, each tagged with a severity tier */
  readonly alwaysDangerous: readonly DangerousPattern[];
  /** Patterns dangerous without WHERE clause (always high severity) */
  readonly dangerousWithoutWhere: readonly UnboundedDmlPattern[];
  /** Engine-specific dangerous patterns (always high severity) */
  readonly engineSpecific: readonly EngineSpecificPattern[];
  /** Custom message generator for confirmation dialog */
  readonly getConfirmationMessage?: (query: string, matchedPattern: string) => string;
};

/**
 * Connection error patterns for detecting lost connections.
 * Engine-specific patterns to identify when a connection has been lost.
 */
export type ConnectionErrorPatterns = {
  /** Patterns that indicate the connection was lost or is unavailable */
  readonly connectionLost: readonly RegExp[];
};

/**
 * UI/Display properties for the engine.
 */
export type EngineDisplayInfo = {
  readonly displayName: string;
  readonly shortName: string;
  readonly icon: string;
  readonly defaultPort: number;
  readonly connectionStringExample: string;
};

/** Which group of the New Connection dialog's engine list an engine belongs to. */
export type ConnectionPickerGroup = 'databases' | 'azure';

/**
 * How the New Connection dialog offers an engine (Option C, C0). `label` is explicit rather than derived from
 * `display`: Kusto's short name is "KQL", but its card says "Azure Data Explorer".
 */
export type ConnectionPickerInfo = {
  readonly label: string;
  readonly group: ConnectionPickerGroup;
  /** One line under the label: what the form asks for. */
  readonly blurb: string;
  /** The form pane's header line (C1): what the engine covers. */
  readonly summary: string;
  /** Other words the start view's filter matches, space-separated (aliases such as "postgres pg"). */
  readonly keywords: string;
  /** Position in the dialog's list (ascending, across groups). */
  readonly order: number;
};

/**
 * Complete engine definition.
 */
export type EngineDefinition = {
  readonly id: DatabaseEngineId;
  readonly display: EngineDisplayInfo;
  /** Absent for engines the New Connection dialog never offers (`mock`). */
  readonly connectionPicker?: ConnectionPickerInfo;
  readonly capabilities: CapabilityCategories;
  readonly dangerousQueryPatterns: DangerousQueryPatterns;
  readonly connectionErrorPatterns: ConnectionErrorPatterns;
  /** Explicit parity gaps - documents known differences vs reference implementation */
  readonly parityGaps: readonly ParityGap[];
};

/**
 * Result of dangerous query check.
 *
 * `severity` is set whenever `dangerous` is `true`. The dialog uses it to
 * pick the title/explanation copy and exposes it as `data-severity` for
 * tests to assert the warning tier without coupling to copy.
 */
/**
 * WHY a statement needs confirming, as a fixed vocabulary.
 *
 * The `reason` above it is prose written for the user and read back by
 * `DangerousQueryDialog`'s copy; this is the stable, low-cardinality fact —
 * the only form of "why" that may reach analytics, and the one thing that can
 * tell a newly-added confirmation apart from a long-standing one when asking
 * whether it is helping or being reflexively dismissed.
 */
export type DangerousQueryKind =
  | 'destructive-ddl'
  | 'routine-drop'
  | 'column-data-loss'
  | 'missing-where'
  | 'engine-specific'
  | 'unparseable'
  | 'agent-destructive'
  | 'agent-opaque';

export type DangerousQueryResult = {
  readonly dangerous: boolean;
  readonly severity?: 'high' | 'low';
  readonly kind?: DangerousQueryKind;
  readonly reason?: string;
  readonly pattern?: string;
  readonly statement?: string;
  /** Stable reason classification (WS-E) — additive; the dialog ignores it,
   *  the equivalence gate and future policy consumers key off it. */
  readonly reasonClass?: RiskReasonClass;
  /** Ordinal of the matched executable statement in the flattened grammar
   *  split (additive; absent for `parse-ambiguous` verdicts, which attribute
   *  no statement). */
  readonly statementIndex?: number;
  /**
   * For a `routine-drop` verdict: the object keywords the script actually
   * drops, lower-cased, in first-appearance order — `['procedure']`,
   * `['view', 'procedure']`, `['package']`…
   *
   * Present only when the low-severity rule fired, and read out of that rule's
   * own capture group (see `collectDroppedObjectTypes`) so it can never
   * disagree with the verdict. The dialog turns it into user vocabulary
   * ("stored procedure"), which is what the reporter asked for: he was told
   * "Routine" and had to work out what that meant mid-decision.
   *
   * Deliberately NOT a closed union: the keyword set differs per engine
   * (MySQL adds EVENT, Oracle adds PACKAGE/TYPE) and lives in
   * `engineDefinitions.ts`. A closed union here would have to be edited every
   * time an engine's pattern changes, and would silently drop the new keyword
   * until it was.
   */
  readonly droppedObjectTypes?: readonly string[];
};
